import type { OrbMotion, OrbState } from "./orb.types.js";

// Resting values for each state. Audio then adds on top while speaking.
type Preset = Omit<OrbMotion, "audioIntensity" | "hue" | "bandLow" | "bandMid" | "bandHigh">;
export const STATE_PRESETS: Record<OrbState, Preset> = {
  idle:      { scale: 1.0,  turbulence: 0.25, flowSpeed: 0.55, glowStrength: 0.55, rimStrength: 0.75, brightness: 0.92 },
  listening: { scale: 1.02, turbulence: 0.40, flowSpeed: 1.10, glowStrength: 0.80, rimStrength: 1.10, brightness: 1.05 },
  thinking:  { scale: 1.0,  turbulence: 0.75, flowSpeed: 1.75, glowStrength: 0.95, rimStrength: 0.90, brightness: 1.00 },
  speaking:  { scale: 1.0,  turbulence: 0.50, flowSpeed: 1.20, glowStrength: 0.85, rimStrength: 0.95, brightness: 1.00 },
};

// How much each unit of audio level adds while speaking.
export const AUDIO_GAIN = {
  scale: 0.07,
  turbulence: 0.55,
  flowSpeed: 0.8,
  glowStrength: 0.55,
  rimStrength: 0.40,
  brightness: 0.14,
} as const;

// Seconds. Attack is quick so the orb answers the voice; release is slow so silences between
// phrases fade instead of snapping.
export const TIMING = {
  audioAttack: 0.05,
  audioRelease: 0.5,
  paramEase: 0.55,
  paramEaseLeavingSpeech: 1.1,
} as const;

// Palette from the design brief.
export const COLORS = {
  deepBlue: [0.031, 0.055, 0.314],
  primaryBlue: [0.09, 0.294, 1.0],
  electricBlue: [0.0, 0.361, 1.0],
  cyan: [0.153, 0.651, 1.0],
  purple: [0.286, 0.188, 1.0],
  magenta: [0.639, 0.169, 1.0],
} as const;

// The colour theme rotates around the aurora palette. Each state sits at its own base angle, and
// while speaking the angle also swings with the voice (see OrbMotionController).
export const HUE_BASE: Record<OrbState, number> = {
  idle: 0,          // purple / pink / sky, the GlowCard aurora
  listening: -0.85, // cooler: toward teal and blue
  thinking: 0.75,   // warmer: toward magenta and amber
  speaking: 0,
};
export const HUE_SWING = 0.95; // radians either side of the base while speaking
